import { forwardRef, useCallback, useEffect, type FC } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Container,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { type Audit } from '#packages/data-collection/models/audit';
import { type MutationPageState } from '#packages/data-collection/types/page-state';
import {
  type AuditUploadState,
  type FormLoadState,
  type FormUploadState,
} from '#apps/client-web/features/upload/hooks/use-upload-audit-mutation';
import { useAuth } from '#apps/client-web/features/auth/hooks/use-auth';

export type AuditDetailsUploadDialogProps = {
  audit: Audit;
  uploadState: AuditUploadState;
  uploadMutationState: MutationPageState;
  isUploadSuccess: boolean;
  resetUploadState: () => void;
  onClose: () => void;
  onUpload: () => void;
};

const renderStatusIcon = (state: FormLoadState | FormUploadState) => {
  let IconComponent;

  switch (state) {
    case 'error': {
      IconComponent = CancelOutlinedIcon;
      break;
    }
    case 'success': {
      IconComponent = CheckCircleOutlinedIcon;
      break;
    }
    case 'idle': {
      IconComponent = CheckBoxOutlineBlankIcon;
      break;
    }
    case 'loading':
    case 'pending': {
      IconComponent = SyncOutlinedIcon;
      break;
    }
  }

  return <IconComponent color="inherit" />;
};

const Transition = forwardRef(function Transition(
  props: React.ComponentProps<typeof Slide>,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" enter exit ref={ref} {...props} />;
});

export const AuditDetailsUploadDialog: FC<AuditDetailsUploadDialogProps> = ({
  audit,
  uploadState,
  uploadMutationState,
  isUploadSuccess,
  resetUploadState,
  onClose,
  onUpload,
  ...props
}) => {
  const isPending = uploadMutationState.state === 'pending';
  const isError = uploadMutationState.state === 'error';
  const isSuccess = uploadMutationState.state === 'success';
  const { isSignedIn } = useAuth();
  const theme = useTheme();

  const getItemColor = useCallback(
    (state: FormLoadState | FormUploadState) => {
      switch (state) {
        case 'error': {
          return theme.palette.error.main;
        }
        case 'success': {
          return theme.palette.success.main;
        }
        case 'idle': {
          return theme.palette.text.primary;
        }
        case 'loading':
        case 'pending': {
          return theme.palette.info.main;
        }
      }
    },
    [theme],
  );

  useEffect(() => {
    if (isSignedIn) {
      onUpload();
    }

    return () => resetUploadState();
  }, [isSignedIn]);

  return (
    <Dialog
      open
      fullScreen
      maxWidth="sm"
      slots={{
        transition: Transition,
      }}
      {...props}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close">
            <Close />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Upload Audit
          </Typography>
          {isSignedIn &&
            (isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <IconButton
                edge="end"
                color="inherit"
                disabled={isPending}
                onClick={isSuccess ? onClose : onUpload}>
                <Check color="inherit" />
              </IconButton>
            ))}
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ height: '100%' }}>
        {isSignedIn ? (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" color="textSecondary">
              {isSuccess ? 'Uploaded:' : 'Uploading:'}
            </Typography>
            <Typography variant="body1">{audit.title}</Typography>

            <Box sx={{ mt: 3 }} />

            <Stack spacing={2}>
              <Grid
                container
                spacing={1}
                sx={{ color: getItemColor(uploadState.preparing) }}>
                {renderStatusIcon(uploadState.preparing)}
                <Typography variant="body1">Preparing forms</Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{ color: getItemColor(uploadState.forms) }}>
                {renderStatusIcon(uploadState.forms)}
                <Typography variant="body1">Uploading forms data</Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  color: getItemColor(uploadState.attachments.background),
                }}>
                {renderStatusIcon(uploadState.attachments.background)}
                <Typography variant="body1">
                  Uploading Background form attachments
                </Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  color: getItemColor(uploadState.attachments.mechanical),
                }}>
                {renderStatusIcon(uploadState.attachments.mechanical)}
                <Typography variant="body1">
                  Uploading Mechanical form attachments
                </Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  color: getItemColor(uploadState.attachments['lighting-type']),
                }}>
                {renderStatusIcon(uploadState.attachments['lighting-type'])}
                <Typography variant="body1">
                  Uploading Lighting Type form attachments
                </Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  color: getItemColor(uploadState.attachments['water-fixture']),
                }}>
                {renderStatusIcon(uploadState.attachments['water-fixture'])}
                <Typography variant="body1">
                  Uploading Water Fixture form attachments
                </Typography>
              </Grid>
              <Grid
                container
                spacing={1}
                sx={{
                  color: getItemColor(uploadState.attachments.windows),
                }}>
                {renderStatusIcon(uploadState.attachments.windows)}
                <Typography variant="body1">
                  Uploading Windows form attachments
                </Typography>
              </Grid>
            </Stack>

            {isError && (
              <Typography color="error" variant="subtitle1" sx={{ mt: 4 }}>
                {uploadMutationState.error instanceof Error
                  ? uploadMutationState.error.message
                  : 'Unknown error'}
              </Typography>
            )}
          </Box>
        ) : (
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            sx={{ height: '100%' }}>
            <Box>
              <Typography
                color="textPrimary"
                textAlign="center"
                variant="h6"
                sx={{ mb: 1 }}>
                Sign in needed
              </Typography>
              <Typography
                color="textSecondary"
                textAlign="center"
                variant="subtitle1">
                To be able to upload your audit data, you need to sign in first.
                Please do so from the menu on the main page.
              </Typography>
            </Box>
          </Grid>
        )}
      </Container>
    </Dialog>
  );
};
